import { FileDownload } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { useInvoiceQuery } from '../../../api/gql';
import { currencies, getPriceWithCurrencyString } from '../../../utils/currencies';
import { formatDate } from '../../../utils/format';
import { fromCentesimal } from '../../../utils/transform';
import { InvoiceManagementInvoicePDFDocument } from './InvoiceManagementInvoicePdfDocument';

type Props = {
  invoiceId: string;
};
export const InvoiceManagementInvoiceDownloadButton = ({ invoiceId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useInvoiceQuery(
    {
      id: invoiceId ?? '',
    },
    {
      select: (res) => ({
        ...res.invoice,
        amount: getPriceWithCurrencyString(fromCentesimal(res.invoice.amount ?? 0), res.invoice.currency),
        currency: currencies.find((curr) => curr.value === res.invoice.currency?.toLowerCase())?.label ?? '-',
        issueDate: formatDate(new Date(res.invoice.issueDate)),
        dueDate: formatDate(new Date(res.invoice.dueDate)),
        taxPoint: formatDate(new Date(res.invoice.taxPoint)),
        amountWithoutVat: getPriceWithCurrencyString(
          fromCentesimal(res.invoice.amountWithoutVat ?? 0),
          res.invoice.currency,
        ),
        amountVat: getPriceWithCurrencyString(fromCentesimal(res.invoice.amountVat ?? 0), res.invoice.currency),
        items: res.invoice.items.map((item) => ({
          ...item,
          priceSum: getPriceWithCurrencyString(fromCentesimal(item.priceSum), res.invoice.currency),
          pricePerUnit: getPriceWithCurrencyString(fromCentesimal(item.pricePerUnit), res.invoice.currency),
          priceWithoutVat: getPriceWithCurrencyString(fromCentesimal(item.priceWithoutVat ?? 0), res.invoice.currency),
          vatRate: item.vatRate ? `${item.vatRate}%` : undefined,
        })),
      }),
    },
  );

  if (!data) {
    return null;
  }

  return (
    <PDFDownloadLink
      document={<InvoiceManagementInvoicePDFDocument data={data} />}
      fileName={`upominej-faktura-${data.number}.pdf`}
    >
      {({ loading }) => (
        <LoadingButton startIcon={<FileDownload />} variant={'outlined'} loading={loading || isLoading}>
          {t('invoice.download')}
        </LoadingButton>
      )}
    </PDFDownloadLink>
  );
};
